import type {
  TaskStatus,
  HexColor,
  StatusCategory,
  TaskTypeId,
  LucideIconName,
  ProjectId,
  FolderId,
  MilestoneId,
  DayStr,
  WorkspaceId,
  Label,
  LabelId,
  TaskType,
} from '../../types';

import { fanoutLast } from '../fanout';
import { maxSortKey, snapshot, sortKeyBetween, taskKeys } from '../internal';
import { logActivity } from '../journal';
import { bump, requireCache } from '../runtime';

export function updateTaskStatusDef(args: {
  id: TaskStatus;
  name?: string;
  color?: HexColor;
  category?: StatusCategory;
}): void {
  const cache = requireCache();
  const def = cache.statuses.get(args.id);
  if (def === undefined) {
    throw new Error('Статус не найден');
  }
  const before = { name: def.name, color: def.color, category: def.category };
  const name = args.name === undefined ? def.name : args.name.trim();
  if (name === '') {
    throw new Error('У статуса должно быть имя');
  }
  if (args.category !== undefined && args.category !== def.category) {
    let left = 0;
    for (const other of cache.statuses.values()) {
      if (other.id !== def.id && other.category === def.category) left += 1;
    }
    if (left === 0) {
      throw new Error('В категории должен остаться хотя бы один статус');
    }
    def.category = args.category;
  }
  def.name = name;
  if (args.color !== undefined) def.color = args.color;
  logActivity(cache, {
    mutator: 'status.update',
    entityTable: 'taskType',
    entityId: String(def.id),
    op: 'update',
    before,
    after: { name: def.name, color: def.color, category: def.category },
  });
  bump('statuses', 'tasks', 'board');
}

export function updateTaskType(args: {
  id: TaskTypeId;
  name?: string;
  color?: HexColor;
  icon?: LucideIconName;
}): void {
  const cache = requireCache();
  const type = cache.taskTypes.get(args.id);
  if (type === undefined) {
    throw new Error('Тип задачи не найден');
  }
  const before = { name: type.name, color: type.color, icon: type.icon };
  if (args.name !== undefined) {
    const name = args.name.trim();
    if (name === '') {
      throw new Error('У типа задачи должно быть имя');
    }
    type.name = name;
  }
  if (args.color !== undefined) type.color = args.color;
  if (args.icon !== undefined) type.icon = args.icon;
  logActivity(cache, {
    mutator: 'taskType.update',
    entityTable: 'taskType',
    entityId: String(type.id),
    op: 'update',
    before,
    after: { name: type.name, color: type.color, icon: type.icon },
  });
  bump('workspace', 'tasks', 'board');
}

export function setProjectStatuses(args: { id: ProjectId; statusIds: TaskStatus[] }): void {
  const cache = requireCache();
  const project = cache.projects.get(args.id);
  if (project === undefined) {
    throw new Error('Проект не найден');
  }
  const unique = [...new Set(args.statusIds)];
  const has = (category: StatusCategory): boolean =>
    unique.some((id) => cache.statuses.get(id)?.category === category);
  if (!has('start') || !has('progress') || !has('final')) {
    throw new Error('В проекте нужен хотя бы один статус каждой категории: старт, прогресс, финал');
  }
  const before = { statusIds: project.statusIds };
  project.statusIds = unique;
  project.updatedAt = cache.nowMs();
  logActivity(cache, {
    mutator: 'project.update',
    entityTable: 'project',
    entityId: String(project.id),
    op: 'update',
    before,
    after: { statusIds: project.statusIds },
  });
  bump('projects', 'tasks', 'board');
}

export function setProjectTypes(args: { id: ProjectId; typeIds: TaskTypeId[] }): void {
  const cache = requireCache();
  const project = cache.projects.get(args.id);
  if (project === undefined) {
    throw new Error('Проект не найден');
  }
  const unique = [...new Set(args.typeIds)];
  if (unique.length === 0) {
    throw new Error('В проекте нужен хотя бы один тип задач');
  }
  const before = { typeIds: project.typeIds };
  project.typeIds = unique;
  project.updatedAt = cache.nowMs();
  logActivity(cache, {
    mutator: 'project.update',
    entityTable: 'project',
    entityId: String(project.id),
    op: 'update',
    before,
    after: { typeIds: project.typeIds },
  });
  bump('projects', 'tasks', 'board');
}

export function updateProject(args: {
  id: ProjectId;
  name?: string;
  icon?: LucideIconName;
  color?: HexColor;
  description?: string | null;
}): void {
  const cache = requireCache();
  const project = cache.projects.get(args.id);
  if (project === undefined) {
    throw new Error('Проект не найден');
  }
  const before = {
    name: project.name,
    icon: project.icon,
    color: project.color,
    description: project.description,
  };
  if (args.name !== undefined) {
    const next = args.name.trim();
    if (next.length === 0) {
      throw new Error('Имя проекта не может быть пустым');
    }
    project.name = next;
  }
  if (args.icon !== undefined) project.icon = args.icon;
  if (args.color !== undefined) project.color = args.color;
  if (args.description !== undefined) project.description = args.description;

  logActivity(cache, {
    mutator: 'project.update',
    entityTable: 'project',
    entityId: String(project.id),
    op: 'update',
    before,
    after: {
      name: project.name,
      icon: project.icon,
      color: project.color,
      description: project.description,
    },
    taskIds: [],
  });
  fanoutLast(cache);
  bump(`board:${String(project.id)}`, 'workspace', 'tree');
}

export function updateFolder(args: {
  id: FolderId;
  name?: string;
  icon?: LucideIconName;
  color?: HexColor;
}): void {
  const cache = requireCache();
  const folder = cache.folders.get(args.id);
  if (folder === undefined) {
    throw new Error('Папка не найдена');
  }
  const before = { name: folder.name, icon: folder.icon, color: folder.color };
  if (args.name !== undefined) {
    const next = args.name.trim();
    if (next.length === 0) {
      throw new Error('Имя папки не может быть пустым');
    }
    folder.name = next;
  }
  if (args.icon !== undefined) folder.icon = args.icon;
  if (args.color !== undefined) folder.color = args.color;

  logActivity(cache, {
    mutator: 'folder.update',
    entityTable: 'folder',
    entityId: String(folder.id),
    op: 'update',
    before,
    after: { name: folder.name, icon: folder.icon, color: folder.color },
    taskIds: [],
  });
  fanoutLast(cache);
  bump('workspace', 'tree');
}

export function deleteFolder(args: { id: FolderId }): void {
  const cache = requireCache();
  const folder = cache.folders.get(args.id);
  if (folder === undefined) {
    throw new Error('Папка не найдена');
  }
  const up = folder.parentId;

  const movedFolders: FolderId[] = [];
  for (const child of cache.folders.values()) {
    if (child.parentId === args.id) {
      child.parentId = up;
      movedFolders.push(child.id);
    }
  }
  const movedProjects: string[] = [];
  for (const project of cache.projects.values()) {
    if (project.folderId === args.id) {
      project.folderId = up;
      movedProjects.push(String(project.id));
    }
  }
  cache.folders.delete(args.id);

  logActivity(cache, {
    mutator: 'folder.delete',
    entityTable: 'folder',
    entityId: String(args.id),
    op: 'delete',
    before: { name: folder.name, parentId: folder.parentId },
    after: { movedFolders: movedFolders.length, movedProjects: movedProjects.length },
    taskIds: [],
  });
  fanoutLast(cache);
  bump('workspace', 'tree');
}

export function updateMilestone(args: {
  id: MilestoneId;
  name?: string;
  icon?: LucideIconName;
  color?: HexColor;
  dueOn?: DayStr | null;
  reached?: boolean;
}): void {
  const cache = requireCache();
  const milestone = cache.milestones.get(args.id);
  if (milestone === undefined) {
    throw new Error('Веха не найдена');
  }
  const before = {
    name: milestone.name,
    icon: milestone.icon,
    color: milestone.color,
    dueOn: milestone.dueOn,
  };
  if (args.name !== undefined) {
    const next = args.name.trim();
    if (next.length === 0) {
      throw new Error('Имя вехи не может быть пустым');
    }
    milestone.name = next;
  }
  if (args.icon !== undefined) milestone.icon = args.icon;
  if (args.color !== undefined) milestone.color = args.color;
  if (args.dueOn !== undefined) milestone.dueOn = args.dueOn;
  if (args.reached !== undefined) milestone.completedAt = args.reached ? cache.nowMs() : null;

  logActivity(cache, {
    mutator: 'milestone.update',
    entityTable: 'milestone',
    entityId: String(milestone.id),
    op: 'update',
    before,
    after: {
      name: milestone.name,
      icon: milestone.icon,
      color: milestone.color,
      dueOn: milestone.dueOn,
    },
    taskIds: [],
  });
  fanoutLast(cache);
  bump(`board:${String(milestone.projectId)}`, 'workspace', 'tree');
}

export function setActiveWorkspace(args: { workspaceId: WorkspaceId }): void {
  const cache = requireCache();
  if (!cache.workspaces.has(args.workspaceId)) {
    throw new Error('Воркспейс не найден');
  }
  if (cache.activeWorkspaceId === args.workspaceId) return;
  cache.activeWorkspaceId = args.workspaceId;
  const keys = new Set<string>(['inbox', 'presence', 'workspace']);
  for (const project of cache.projects.values()) {
    keys.add(`board:${String(project.id)}`);
  }
  for (const channel of cache.channels.values()) {
    keys.add(`channel:${String(channel.id)}`);
  }
  bump(...keys);
}

const NEW_LABEL_COLORS = [1, 2, 3, 4, 5, 6, 7, 8].map((index) => `var(--identity-${index})`);

export function createLabel(args: { id: LabelId; name: string; color?: HexColor }): Label {
  const cache = requireCache();
  const name = args.name.trim();
  if (name === '') {
    throw new Error('Имя лейбла не может быть пустым');
  }
  const color = args.color ?? (NEW_LABEL_COLORS[cache.labels.size % NEW_LABEL_COLORS.length] as HexColor);
  const label: Label = {
    id: args.id,
    workspaceId: cache.activeWorkspaceId,
    name,
    color,
    createdBy: cache.me,
    archivedAt: null,
  };
  cache.labels.set(label.id, label);
  logActivity(cache, {
    mutator: 'label.create',
    entityTable: 'label',
    entityId: String(label.id),
    op: 'insert',
    before: null,
    after: snapshot(label),
    taskIds: [],
  });
  bump('workspace', 'tasks', 'board');
  return label;
}

export function updateLabel(args: { id: LabelId; name?: string; color?: HexColor }): void {
  const cache = requireCache();
  const label = cache.labels.get(args.id);
  if (label === undefined) {
    throw new Error('Лейбл не найден');
  }
  const before = { name: label.name, color: label.color };
  if (args.name !== undefined) {
    const name = args.name.trim();
    if (name === '') {
      throw new Error('Имя лейбла не может быть пустым');
    }
    label.name = name;
  }
  if (args.color !== undefined) label.color = args.color;
  logActivity(cache, {
    mutator: 'label.update',
    entityTable: 'label',
    entityId: String(label.id),
    op: 'update',
    before,
    after: { name: label.name, color: label.color },
    taskIds: [],
  });
  bump('workspace', 'tasks', 'board');
}

export function deleteLabel(args: { id: LabelId }): void {
  const cache = requireCache();
  const label = cache.labels.get(args.id);
  if (label === undefined || label.archivedAt !== null) {
    throw new Error('Лейбл не найден');
  }
  const now = cache.nowMs();
  const affected = [...cache.tasks.values()].filter((task) => task.labelIds.includes(label.id));
  for (const task of affected) {
    task.labelIds = task.labelIds.filter((id) => id !== label.id);
    task.updatedAt = now;
  }
  label.archivedAt = now;
  logActivity(cache, {
    mutator: 'label.delete',
    entityTable: 'label',
    entityId: String(label.id),
    op: 'update',
    before: { archivedAt: null },
    after: { archivedAt: now },
    taskIds: affected.map((task) => task.id),
  });
  bump('workspace', 'tasks', 'board', ...affected.flatMap((task) => taskKeys(cache, task.id)));
}

export function createTaskType(args: { id: TaskTypeId; name: string; icon?: LucideIconName; color?: HexColor }): TaskType {
  const cache = requireCache();
  const name = args.name.trim();
  if (name === '') {
    throw new Error('Имя типа не может быть пустым');
  }
  const existing = [...cache.taskTypes.values()].filter((type) => type.archivedAt === null);
  const template = existing[0];
  const type: TaskType = {
    id: args.id,
    workspaceId: cache.activeWorkspaceId,
    name,
    icon: args.icon ?? template?.icon ?? ('Shapes' as LucideIconName),
    color: args.color ?? template?.color ?? ('var(--identity-1)' as HexColor),
    sortKey: sortKeyBetween(maxSortKey(existing.map((item) => item.sortKey)), null),
    archivedAt: null,
  };
  cache.taskTypes.set(type.id, type);
  const now = cache.nowMs();
  for (const project of cache.projects.values()) {
    if (project.archivedAt !== null) continue;
    project.typeIds = [...project.typeIds, type.id];
    project.updatedAt = now;
  }
  logActivity(cache, {
    mutator: 'taskType.create',
    entityTable: 'taskType',
    entityId: String(type.id),
    op: 'insert',
    before: null,
    after: snapshot(type),
  });
  bump('workspace', 'projects', 'tasks', 'board');
  return type;
}

export function deleteTaskType(args: { id: TaskTypeId }): void {
  const cache = requireCache();
  const type = cache.taskTypes.get(args.id);
  if (type === undefined || type.archivedAt !== null) {
    throw new Error('Тип задачи не найден');
  }
  const projects = [...cache.projects.values()].filter((project) => project.archivedAt === null);
  const soleOf = projects.find((project) => project.typeIds.length === 1 && project.typeIds[0] === type.id);
  if (soleOf !== undefined) {
    throw new Error(`Это последний тип проекта «${soleOf.name}»`);
  }
  const remaining = [...cache.taskTypes.values()].filter((other) => other.archivedAt === null && other.id !== type.id);
  if (remaining.length === 0) {
    throw new Error('Нужен хотя бы один тип задачи');
  }
  const now = cache.nowMs();
  const affected = [...cache.tasks.values()].filter((task) => task.typeId === type.id);
  for (const task of affected) {
    task.typeId = null;
    task.updatedAt = now;
  }
  for (const project of cache.projects.values()) {
    if (!project.typeIds.includes(type.id)) continue;
    project.typeIds = project.typeIds.filter((id) => id !== type.id);
    project.updatedAt = now;
  }
  type.archivedAt = now;
  logActivity(cache, {
    mutator: 'taskType.delete',
    entityTable: 'taskType',
    entityId: String(type.id),
    op: 'update',
    before: { archivedAt: null },
    after: { archivedAt: now },
    taskIds: affected.map((task) => task.id),
  });
  bump('workspace', 'projects', 'tasks', 'board', ...affected.flatMap((task) => taskKeys(cache, task.id)));
}
